import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, Check, X } from "lucide-react";
import SpeakerButton from "./SpeakerButton";
import { fetchAssessmentBank, submitAssessment, fetchAssessmentHistory } from "../lib/api";

export const AssessmentModal = ({ open, onClose, onComplete }) => {
  const [bank, setBank] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(null); // result
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (open) {
      fetchAssessmentBank().then(d => { setBank(d.items || []); setIdx(0); setAnswers([]); setDone(null); });
      fetchAssessmentHistory().then(setHistory).catch(() => setHistory([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  const item = bank[idx];

  const pick = (option) => {
    const correct = option === item.answer;
    const next = [...answers, { word: item.word, level: item.level, correct }];
    setAnswers(next);
    if (idx + 1 < bank.length) {
      setIdx(idx + 1);
    } else {
      // submit
      submitAssessment(next).then(res => {
        setDone(res);
        onComplete && onComplete(res);
      });
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        data-testid="assessment-modal"
      >
        <motion.div
          initial={{ scale: 0.92, y: 16 }}
          animate={{ scale: 1, y: 0 }}
          className="eigoo-glass rounded-3xl p-6 md:p-10 w-full max-w-2xl"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-yellow-300 font-display font-bold tracking-widest text-xs">
              <Sparkles size={14} /> READING ASSESSMENT
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="close-assessment"><X /></button>
          </div>

          {!done ? (
            <>
              <h3 className="font-display font-black text-3xl md:text-4xl mb-1">
                Pick the correct word
              </h3>
              <div className="text-white/60 text-sm mb-6 flex items-center gap-2">
                Question {idx + 1} of {bank.length}
                <span className="px-2 py-0.5 rounded-full bg-yellow-300/10 border border-yellow-300/30 text-yellow-200 text-[10px] uppercase tracking-widest">
                  Level {item ? item.level : "—"}
                </span>
              </div>

              {item && (
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="font-display font-black text-5xl md:text-6xl text-yellow-200">"{item.answer}"</div>
                    <SpeakerButton text={`Find the word: ${item.answer}.`} testid="speaker-assessment-prompt" size="lg" voice="fable" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {item.options.map((opt) => (
                      <button
                        key={opt}
                        onClick={() => pick(opt)}
                        data-testid={`assessment-option-${opt}`}
                        className="btn-eigoo btn-ghost-gold text-xl md:text-2xl py-5"
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center" data-testid="assessment-result">
              <div className="font-display font-black text-3xl md:text-4xl mb-2">Cadet level locked in!</div>
              <div className="text-white/70 mb-5">You scored <span className="text-yellow-300 font-bold">{done.score}/{done.total}</span></div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-300/10 border border-yellow-300/40 mb-6">
                <span className="text-yellow-200 font-display font-bold text-2xl">Reading Level: {done.level.toFixed(1)}</span>
                <SpeakerButton text={`Your reading level is ${done.level.toFixed(1)}. Great work, Commander!`} testid="speaker-assessment-result" />
              </div>
              {history.length > 0 && (
                <div className="text-xs text-white/50 mb-4">Previous: {history.slice(-1)[0]?.level.toFixed(1)}</div>
              )}
              <div className="grid sm:grid-cols-2 gap-4 text-left">
                <div className="rounded-2xl bg-green-500/10 border border-green-500/30 p-4">
                  <div className="text-green-300 font-display font-bold text-sm mb-2 flex items-center gap-1">
                    <Check size={16} /> Words you know
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {done.words_known.map(w => <span key={w} className="px-2 py-0.5 rounded-md bg-green-500/15 text-green-200 text-sm">{w}</span>)}
                  </div>
                </div>
                <div className="rounded-2xl bg-yellow-300/10 border border-yellow-300/30 p-4">
                  <div className="text-yellow-300 font-display font-bold text-sm mb-2 flex items-center gap-1">
                    <ArrowRight size={16} /> Power words to learn
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {done.words_to_learn.map(w => <span key={w} className="px-2 py-0.5 rounded-md bg-yellow-300/15 text-yellow-200 text-sm">{w}</span>)}
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="btn-eigoo btn-gold mt-6" data-testid="assessment-continue">
                Enter the Bridge <ArrowRight size={18} />
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AssessmentModal;
